import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";

export type ControlLayerKey = "sprache" | "fachwissen" | "kunde" | "bewegung";
export type PackageType = "industry" | "process" | "kpi" | "use_case";
export type Priority = "critical" | "high" | "medium" | "low";

export type ControlLayer = {
  key: ControlLayerKey;
  label: string;
  purpose: string;
  examples: string[];
  object_count?: number;
};

export type ControlStage = { key: string; label: string; object_type: string };

export type ControlContract = {
  version: string;
  layers: ControlLayer[];
  language: Array<{ id: string; term: string; definition: string; layer: "sprache" }>;
  stages: ControlStage[];
  kpi_required_fields: Array<{ field: string; label: string }>;
  intake_modes: string[];
  handoff_rules: string[];
  invariants: {
    tenant_scoped: boolean;
    evidence_required: boolean;
    external_writes: boolean;
    measurements_separate_from_definitions: boolean;
  };
};

export type ControlOverview = {
  status: string;
  persistent: boolean;
  external_writes: false;
  gold_write: false;
  layers: ControlLayer[];
  unassigned_layer_count: number;
  packages: { total: number; by_type: Record<PackageType, number>; customer_adoptions: number };
  kpis: { total: number; complete: number; incomplete: number };
  cases: { total: number; live: number; reference: number };
  decision_queue: { open: number; critical: number; high: number };
  counts: Record<string, number>;
};

export type ControlPackage = {
  id: string;
  layer: "fachwissen" | "kunde";
  package_type: PackageType;
  parent_id?: string;
  title: string;
  summary: string;
  version: number;
  status: string;
  payload: Record<string, unknown>;
  evidence_refs: string[];
  confidence: number;
  owner: string;
  provenance: string;
  created_at?: string;
  updated_at?: string;
};

export type KpiCompleteness = {
  complete: boolean;
  score_pct: number;
  passed: number;
  total: number;
  missing: Array<{ field: string; label: string }>;
  checks: Array<{ field: string; label: string; ok: boolean }>;
};

export type KpiDefinition = {
  id: string;
  layer: "fachwissen" | "kunde";
  package_id?: string;
  metric_definition_id?: string;
  metric_key: string;
  name: string;
  definition: string;
  formula: string;
  unit: string;
  period: string;
  direction: "up_is_favorable" | "down_is_favorable" | "neutral";
  dimensions: string[];
  required_data: string[];
  source_refs: string[];
  target: { value?: number; tolerance_pct?: number };
  warning_thresholds: { warning_pct?: number; critical_pct?: number; lower?: number; upper?: number };
  benchmark: { value?: number; unit?: string; source?: string };
  owner: string;
  evidence_refs: string[];
  confidence: number;
  status: string;
  version: number;
  completeness: KpiCompleteness;
  created_at?: string;
  updated_at?: string;
};

export type IntakeItem = {
  id: string;
  item_key: string;
  mode: "automatic" | "suggested" | "human_decision";
  question: string;
  proposal: unknown;
  value: unknown;
  status: string;
  owner: string;
  confidence: number;
  evidence_refs: string[];
};

export type ControlObject = {
  id: string;
  case_id: string;
  layer: "bewegung";
  object_type: string;
  sequence: number;
  iteration: number;
  title: string;
  status: string;
  owner: string;
  confidence: number;
  evidence_refs: string[];
  payload: Record<string, unknown>;
  generated_by: string;
  automated: boolean;
  human_required: boolean;
  created_at?: string;
  updated_at?: string;
};

export type DecisionItem = {
  id: string;
  case_id: string;
  trigger_key: string;
  priority: Priority;
  title: string;
  reason: string;
  status: "offen" | "in_pruefung" | "erledigt";
  owner: string;
  object_id: string;
  evidence_refs: string[];
  confidence: number;
  due_at?: string | null;
  resolution: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
};

export type ControlCase = {
  id: string;
  title: string;
  scenario_type: "live" | "synthetic_reference";
  status: string;
  current_stage: string;
  iteration: number;
  package_ids: string[];
  kpi_definition_id: string;
  source_ref: string;
  owner: string;
  evidence_refs: string[];
  confidence: number;
  context: Record<string, unknown>;
  created_by: string;
  created_at?: string;
  updated_at?: string;
};

export type ControlCaseDetail = ControlCase & {
  kpi_definition: KpiDefinition | null;
  objects: ControlObject[];
  links: Array<{
    id: string;
    case_id: string;
    from_object_id: string;
    to_object_id: string;
    relation: string;
    evidence_refs: string[];
  }>;
  decision_items: DecisionItem[];
  events: Array<{ id: string; event_type: string; actor: string; payload: Record<string, unknown>; created_at: string }>;
  stage_counts: Record<string, number>;
  chain_valid: boolean;
  invalid_links: string[];
  reference_verification?: {
    synthetic: true;
    gold_write: false;
    external_write: false;
    required_object_types: string[];
    present_object_types: string[];
    complete: boolean;
  };
};

export type CreateCaseInput = {
  title: string;
  package_ids: string[];
  kpi_definition_id: string;
  source_ref: string;
  owner: string;
  evidence_refs: string[];
  confidence: number;
  context: Record<string, unknown>;
};

function useInvalidateControlSystem() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["control-system"] });
    queryClient.invalidateQueries({ queryKey: ["start-control-system"] });
  };
}

function useControlQuery<T>(suffix: string, path: string, staleTime = 15_000) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["control-system", effectiveTenantId, suffix],
    queryFn: () => api.get<T>(path),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime,
  });
}

export function useControlContract() {
  return useControlQuery<ControlContract>("contract", "/control-system/contract", 300_000);
}

export function useControlOverview() {
  return useControlQuery<ControlOverview>("overview", "/control-system/overview");
}

export function useControlPackages() {
  return useControlQuery<ControlPackage[]>("packages", "/control-system/packages", 30_000);
}

export function useControlKpis() {
  return useControlQuery<KpiDefinition[]>("kpis", "/control-system/kpi-definitions", 30_000);
}

export function useControlIntake() {
  return useControlQuery<IntakeItem[]>("intake", "/control-system/intake");
}

export function useControlCases(includeReference = true) {
  return useControlQuery<ControlCase[]>(
    `cases:${includeReference ? "all" : "live"}`,
    `/control-system/cases?include_reference=${includeReference ? "true" : "false"}`,
  );
}

export function useControlCase(caseId: string | null) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["control-system", effectiveTenantId, "case", caseId],
    queryFn: () => api.get<ControlCaseDetail>(`/control-system/cases/${encodeURIComponent(caseId ?? "")}`),
    enabled: Boolean(effectiveTenantId && caseId),
    retry: 1,
    staleTime: 5_000,
  });
}

export function useControlDecisions(status = "open") {
  return useControlQuery<DecisionItem[]>(
    `decisions:${status}`,
    `/control-system/decisions?status=${encodeURIComponent(status)}`,
  );
}

export function useAdoptControlPackage() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      api.post<{ package: ControlPackage; kpi_definition: KpiDefinition | null }>(
        `/control-system/packages/${encodeURIComponent(id)}/adopt`,
        input,
      ),
    onSuccess: invalidate,
  });
}

export function useSaveControlKpi() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<KpiDefinition>("/control-system/kpi-definitions", input),
    onSuccess: invalidate,
  });
}

export function useDeriveControlIntake() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ items: IntakeItem[]; summary: Record<string, number> }>("/control-system/intake/derive", input),
    onSuccess: invalidate,
  });
}

export function useUpdateControlIntake() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      api.patch<IntakeItem>(`/control-system/intake/${encodeURIComponent(id)}`, input),
    onSuccess: invalidate,
  });
}

export function useCreateControlCase() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: (input: CreateCaseInput) => api.post<ControlCaseDetail>("/control-system/cases", input),
    onSuccess: invalidate,
  });
}

export function useRunControlCase() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: (caseId: string) =>
      api.post<ControlCaseDetail>(`/control-system/cases/${encodeURIComponent(caseId)}/run`, {}),
    onSuccess: invalidate,
  });
}

export function useDecideControlCase() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      api.post<ControlCaseDetail>(`/control-system/cases/${encodeURIComponent(id)}/decision`, input),
    onSuccess: invalidate,
  });
}

export function useMeasureControlImpact() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      api.post<ControlCaseDetail>(`/control-system/cases/${encodeURIComponent(id)}/impact`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateControlDecision() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      api.patch<DecisionItem>(`/control-system/decisions/${encodeURIComponent(id)}`, input),
    onSuccess: invalidate,
  });
}

export function useCreateReferenceCase() {
  const api = useApi();
  const invalidate = useInvalidateControlSystem();
  return useMutation({
    mutationFn: (reset: boolean) =>
      api.post<ControlCaseDetail>("/control-system/reference-case", { reset }),
    onSuccess: invalidate,
  });
}
